import React, {useEffect, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Message} from "primereact/message";
import {Button} from "primereact/button";

import {AppContext} from "../../HomePage";
import {SearchDiagnosticsService} from "../../service/SearchDiagnosticsService";
import {isAdmin} from "../../utils/Utils";
import SpiderFixPromptDialog from "./SpiderFixPromptDialog";
import RetryFailedUrlsDialog from "./RetryFailedUrlsDialog";

const service = new SearchDiagnosticsService();

// Admin-only: the campaign's LATEST run, aggregated to answer a question no single
// product can - "what should we fix next".
//
// Everything here leads with PRODUCTS rather than URLs. A site that fails forty times on
// one product is one product's worth of work; a site that fails once each on forty
// products is forty, and only that second number tells an admin where a morning is best
// spent. URL counts are still shown beside them, because they say how noisy a failure is,
// but they are never what the rows are ranked or read by.
const CampaignSearchSummary = ({campaignId, reloadToken}) => {

    const {t, setError} = React.useContext(AppContext);
    const [data, setData] = useState(null);
    const [promptVisible, setPromptVisible] = useState(false);
    const [retryVisible, setRetryVisible] = useState(false);

    useEffect(() => {
        if (!isAdmin() || !campaignId) return;
        service.getCampaignSearchSummary(campaignId)
            .then(setData)
            .catch(error => { setError(error); setError(null); });
    }, [campaignId, reloadToken]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!isAdmin() || !data) return null;

    const label = (key) => t(`search_diagnostics.${key}`);
    const productsByReason = data.products_by_drop_reason || {};
    const dropped = Object.entries(data.counts?.dropped_by_reason || {})
        .map(([outcome, urls]) => ({outcome, urls, products: productsByReason[outcome] || 0}))
        .sort((a, b) => b.products - a.products || b.urls - a.urls);

    return <div style={{marginTop: '1.5em'}}>
        <SpiderFixPromptDialog visible={promptVisible} onHide={() => setPromptVisible(false)}
                               data={data} campaignId={campaignId}/>
        <RetryFailedUrlsDialog visible={retryVisible} onHide={() => setRetryVisible(false)}
                               data={data} campaignId={campaignId}/>
        <h6>{t('product_detail.search_diagnostics')}
            <span style={{fontWeight: 'normal', marginLeft: '.6em', fontSize: '.85em',
                          color: 'var(--text-color-secondary)'}}>
                {data.run?.started_at && new Date(data.run.started_at).toLocaleString()}
                {data.run && <> · {data.run.engine} · {data.run.trigger}</>}
                {!data.run && <> · {label('run_expired')}</>}
                {' · '}{data.products} {label('products_processed')}
                {' · '}{data.counts?.suggested} {label('suggested')}
                {' · '}{data.suggestions_per_product} {label('suggestions_per_product')}
            </span>
            {((data.top_broken_hosts || []).length > 0 || (data.parsed_hosts || []).length > 0) &&
                <Button label={t('search_diagnostics.fix_prompt')} icon="pi pi-wrench"
                        className="p-button-sm p-button-outlined"
                        style={{marginLeft: '.75em'}}
                        onClick={() => setPromptVisible(true)}/>}
            {(data.top_broken_hosts || []).length > 0 &&
                <Button label={t('search_diagnostics.retry_failed')} icon="pi pi-replay"
                        className="p-button-sm p-button-outlined"
                        style={{marginLeft: '.5em'}}
                        onClick={() => setRetryVisible(true)}/>}
        </h6>

        {/* A retry does not change what the SEARCH found - the tables below still describe
            the search run - so it gets its own line rather than being folded into them.
            Without this the retry would be invisible here, since no product document ever
            points at a retry run. */}
        {data.last_retry && <div style={{marginBottom: '.5em',
                 color: 'var(--text-color-secondary)', fontSize: '.85em'}}>
            {label('last_retry')}: {new Date(data.last_retry.started_at).toLocaleString()}
            {data.last_retry.totals && <> · {data.last_retry.totals.suggested || 0} {label('recovered')}</>}
        </div>}

        {/* Products the engine never answered for at all - every page blocked, errored
            or rejected. They produce no results, so they contribute nothing to the drop
            reasons below and would otherwise be invisible. This is the one line here
            that does NOT point at a spider: it points at the engine or the proxy. */}
        {data.products_never_searched > 0 && <Message severity="warn"
                 text={`${data.products_never_searched} ${label('products_never_searched')}`}
                 style={{marginBottom: '.5em', display: 'block'}}/>}

        {/* Searched properly and still came away with nothing. Deliberately separate from
            the line above: these are ours to fix, those are the engine's. */}
        {data.products_with_no_suggestion > 0 && <Message severity="info"
                 text={`${data.products_with_no_suggestion} ${label('products_with_no_suggestion')}`}
                 style={{marginBottom: '.75em', display: 'block'}}/>}

        <div style={{display: 'flex', gap: '2em', flexWrap: 'wrap'}}>
            <div style={{flex: '1 1 340px'}}>
                <DataTable value={dropped} responsiveLayout="scroll" className="p-datatable-sm">
                    <Column header={label('dropped')}
                            body={(row) => t(`search_diagnostics.outcome_${row.outcome}`)}/>
                    <Column field="products" header={label('products_column')}
                            style={{width: '7rem'}}/>
                    <Column field="urls" header={label('urls_column')} style={{width: '6rem'}}/>
                </DataTable>
            </div>
            <div style={{flex: '1 1 340px'}}>
                <DataTable value={data.top_broken_hosts || []} responsiveLayout="scroll"
                           className="p-datatable-sm">
                    <Column field="host" header={label('top_broken_hosts')}/>
                    <Column header={label('reason')}
                            body={(row) => t(`search_diagnostics.outcome_${row.reason}`)}/>
                    <Column field="products" header={label('products_column')}
                            style={{width: '7rem'}}/>
                    <Column field="urls" header={label('urls_column')} style={{width: '6rem'}}/>
                </DataTable>
            </div>
        </div>
    </div>;
};

export default CampaignSearchSummary;
